import React, { useState, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProject } from '@adsk/alloy-provider';
import { ActivityCard } from '@adsk/alloy-react-activity-log';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { ACTIVITY_VARIANTS } from '../constants';
import { getThumbnail } from './helpers/utils';

const SheetActivityCard = ({ entity, variant, ...rest }) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const [thumbnail, setThumbnail] = useState(null);
  const currentProject = useCurrentProject();
  const scopes = currentProject?.scopes?.project;

  const fetchThumbnail = useCallback(async () => {
    const blob = await getThumbnail(
      entity.bubbleUrn,
      entity.bubbleViewableGuid,
      scopes,
    );
    setThumbnail(blob);
  }, [entity.bubbleUrn, entity.bubbleViewableGuid, scopes]);

  useEffect(() => {
    entity.bubbleUrn &&
      entity.bubbleViewableGuid &&
      !thumbnail &&
      fetchThumbnail();
  }, [entity.bubbleUrn, entity.bubbleViewableGuid, thumbnail, fetchThumbnail]);

  useEffect(() => {
    return () => {
      thumbnail && URL.revokeObjectURL(thumbnail);
    };
  }, [thumbnail]);

  const title = T.translate(
    variant === ACTIVITY_VARIANTS.ADDED
      ? 'SHEET_ACTIVITY_CARD_ADD'
      : 'SHEET_ACTIVITY_CARD_REMOVE',
  );

  return (
    <ActivityCard title={title} {...rest}>
      {entity?.name && (
        <Flex alignItems="center">
          <Flex
            marginRight="10px"
            border={`1px solid ${theme.colors.charcoal100}`}
            flex="none"
            alignItems="center"
            justifyContent="center"
            width="40px"
            height="40px"
          >
            {thumbnail ? (
              <img width="40" height="40" src={thumbnail} />
            ) : (
              <Box width="100%" height="100%" backgroundColor="charcoal050" />
            )}
          </Flex>
          <Box flex="1" width="80%">
            <OverflowTooltip
              getTargetProps={() => ({
                style: { overflow: 'hidden', flex: 'auto' },
              })}
              content={entity.name}
            >
              <span style={theme.typography.bodyMediumBold}>{entity.name}</span>
            </OverflowTooltip>
            <Flex flexDirection="row">
              <OverflowTooltip
                getTargetProps={() => ({
                  style: {
                    overflow: 'hidden',
                    maxWidth: '100%',
                    color: theme.colors.charcoal900,
                  },
                })}
                content={entity.versionSet?.name}
              >
                <span style={theme.typography.bodySmall}>
                  {entity.versionSet?.name}
                </span>
              </OverflowTooltip>
              {entity.collection?.name &&
                entity.collection.type !== 'default' && (
                  <>
                    <Text color="charcoal400" padding="0px 12px">
                      |
                    </Text>
                    <OverflowTooltip
                      getTargetProps={() => ({
                        style: {
                          overflow: 'hidden',
                          maxWidth: '100%',
                          color: theme.colors.charcoal900,
                        },
                      })}
                      content={entity.collection.name}
                    >
                      {entity.collection.name}
                    </OverflowTooltip>
                  </>
                )}
            </Flex>
            {entity.title && (
              <OverflowTooltip
                getTargetProps={() => ({
                  style: {
                    overflow: 'hidden',
                    flex: 1,
                    color: theme.colors.charcoal900,
                  },
                })}
                content={entity.title}
              >
                <span style={theme.typography.bodySmall}>{entity.title}</span>
              </OverflowTooltip>
            )}
          </Box>
        </Flex>
      )}
    </ActivityCard>
  );
};

SheetActivityCard.propTypes = {
  entity: PropTypes.object,
  variant: PropTypes.oneOf(Object.values(ACTIVITY_VARIANTS)),
};

export default SheetActivityCard;
